// Decided once, synchronously, before anything else in index.tsx runs.
// Stage mode = the app is being shown inside an Academy lesson's player
// (same-origin iframe) and must talk to the fixture API instead of the
// server. A top-level window only enters stage mode with the explicit
// `harness=1` flag, which is the authoring harness for scenes.
import { STAGE_HARNESS_PARAM, STAGE_QUERY_PARAM, STAGE_WINDOW_NAME_PREFIX } from "./protocol";

function detect(): { active: boolean; sceneId: string | null; harness: boolean; resumeStep: number | null } {
  const none = { active: false, sceneId: null, harness: false, resumeStep: null };
  if (typeof window === "undefined") return none;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return none;
  }
  const harness = params.get(STAGE_HARNESS_PARAM) === "1";
  const framed = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();
  // After a seek the stage reloads itself without the query string, so the
  // scene id is also carried in window.name (never in sessionStorage, which a
  // same-origin iframe shares with the parent tab). A seek appends
  // `#<stepIndex>`: the step to fast-forward to before reporting ready.
  let fromName: string | null = null;
  let resumeStep: number | null = null;
  if (window.name && window.name.startsWith(STAGE_WINDOW_NAME_PREFIX)) {
    const rest = window.name.slice(STAGE_WINDOW_NAME_PREFIX.length);
    const hash = rest.indexOf("#");
    if (hash === -1) {
      fromName = rest;
    } else {
      fromName = rest.slice(0, hash);
      const n = parseInt(rest.slice(hash + 1), 10);
      resumeStep = Number.isFinite(n) && n > 0 ? n : null;
    }
  }
  const sceneId = params.get(STAGE_QUERY_PARAM) || fromName;
  const active = !!sceneId && (framed || harness);
  // Consume the step suffix so a plain reload starts the scene over.
  if (active) window.name = `${STAGE_WINDOW_NAME_PREFIX}${sceneId}`;
  return {
    active,
    sceneId: active ? sceneId : null,
    harness: active && harness,
    resumeStep: active ? resumeStep : null,
  };
}

const detected = detect();

export const isAcademyStage: boolean = detected.active;
export const academyStageSceneId: string | null = detected.sceneId;
export const isAcademyHarness: boolean = detected.harness;
/** Step index a seek asked the stage to fast-forward to (null = start fresh). */
export const academyStageResumeStep: number | null = detected.resumeStep;

/** The window.name a seek writes before reloading: scene plus the target step. */
export const stageWindowName = (sceneId: string, stepIndex: number): string =>
  stepIndex > 0 ? `${STAGE_WINDOW_NAME_PREFIX}${sceneId}#${stepIndex}` : `${STAGE_WINDOW_NAME_PREFIX}${sceneId}`;
